// Canonical engine wire emit for a WhatsApp contact. The chrome
// extension's `queryContactsAndChats` handler projects WAWeb's raw
// Contact model → this slim shape at the handler boundary. v1's
// /v1/contacts mapper is a pure 1:1 projection from here.
//
// The RAW WAWeb Contact model (with wid id, address-book sync flags,
// pushname/verifiedName/type/isMyContact/isWAContact/isEnterprise/
// isSmb, etc.) lives inside the chrome extension per #74.

import { z } from 'zod';
import { WireJidSchema } from '../../contract/wire-identity';
import { coerceLegacyChatId } from './legacy-id';

const BtSlimContactObjectSchema = z.object({
  /** Bare JID string (engine collapses raw wid to this id). camelCase to
   *  match the chat wire shape (`BtSlimChat.chatId`) so both arrays of the
   *  waQueryContactsAndChatsTool response key the id the same way. */
  chatId: WireJidSchema,
  /** Address-book name (when saved) OR pushname (when not). The slim
   *  emit pre-resolves so consumers don't need a fallback chain. */
  name: z.string().nullable().optional(),
  /** True for SMB or enterprise business accounts. */
  /** @5.0b delete. */
  is_business: z.boolean().optional(),
});
/** Canonical contact wire schema. Wrapped in the same legacy-id coercion as
 *  BtSlimChat so a pre-rename gateway emitting `id`/`_serialized` still
 *  parses — see coerceLegacyChatId. */
export const BtSlimContactSchema = z.preprocess(coerceLegacyChatId, BtSlimContactObjectSchema);
export type BtSlimContact = z.infer<typeof BtSlimContactSchema>;

/**
 * Lean per-entry shape emitted by the extension `getContacts()` minimal mode
 * (`includeExtendedInfo !== true`) and persisted into `wa-contacts-cache`.
 * It is `BtSlimContact` + the kind flags the list view needs. This is the
 * STRICT contract for both the extension emit (producer) and the backend cache
 * save (consumer) — type both against it so the shape cannot silently drift
 * (a change on either side that breaks the contract becomes a compile error).
 */
export const BtLeanListContactSchema = z.preprocess(
  coerceLegacyChatId,
  BtSlimContactObjectSchema.extend({
    /** Group chat entry (`@g.us`). */
    isGroup: z.boolean().optional(),
    /** Newsletter/channel entry (`@newsletter`). */
    isNewsletter: z.boolean().optional(),
  }),
);
export type BtLeanListContact = z.infer<typeof BtLeanListContactSchema>;
